/**
 * PlaneArt.js
 *
 * A Right Brother Airways jet in the sky, for the check-in entry screen. Drawn in SVG in the
 * boarding pass's colours, so it stays sharp at any size and needs no image file.
 */

import React from "react";

const INK = "#1B2A41";
const PAPER = "#FBF7EF";
const RED = "#C8442F";
const BELLY = "#E3DCCD";
const WINDOW = "#2B3B55";

// Cabin windows along the fuselage
const WINDOWS = Array.from({ length: 22 }, (_, i) => 214 + i * 19);

/**
 * A soft cloud made of overlapping circles
 */
const Cloud = ({ x, y, scale = 1, opacity = 0.9 }) => (
    <g transform={`translate(${x} ${y}) scale(${scale})`} fill="#FFFFFF" opacity={opacity}>
        <ellipse cx="0" cy="0" rx="46" ry="16" />
        <circle cx="-18" cy="-10" r="18" />
        <circle cx="10" cy="-16" r="22" />
        <circle cx="34" cy="-6" r="14" />
    </g>
);

/**
 * @param {String} className    -   Sizing, e.g. 'w-full h-full'
 * @returns {JSX.Element}
 */
const PlaneArt = ({ className = "" }) => (
    <svg viewBox="0 0 800 440" className={className} role="img" aria-label="A Right Brother Airways jet flying through the clouds" preserveAspectRatio="xMidYMid meet">
        <defs>
            <linearGradient id="rb-fuselage" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0" stopColor="#FFFFFF" />
                <stop offset="0.62" stopColor={PAPER} />
                <stop offset="1" stopColor={BELLY} />
            </linearGradient>
            <linearGradient id="rb-tail" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor="#24375A" />
                <stop offset="1" stopColor={INK} />
            </linearGradient>
        </defs>

        <Cloud x={130} y={92} scale={1.1} opacity={0.75} />
        <Cloud x={640} y={70} scale={0.8} opacity={0.6} />
        <Cloud x={700} y={372} scale={1.3} opacity={0.85} />
        <Cloud x={120} y={360} scale={0.9} opacity={0.7} />

        {/* Contrails */}
        <path d="M60 238 L-40 252" stroke="#FFFFFF" strokeWidth="5" strokeLinecap="round" opacity="0.6" />
        <path d="M250 300 L120 318" stroke="#FFFFFF" strokeWidth="4" strokeLinecap="round" opacity="0.5" />

        {/* Far wing, behind the fuselage */}
        <path d="M420 212 L520 150 L548 150 L470 214 Z" fill="#B9B2A3" />

        {/* Tail fin and stabiliser */}
        <path d="M118 196 L72 92 L118 92 L198 196 Z" fill="url(#rb-tail)" />
        <path d="M84 104 L112 104 L150 160" stroke={RED} strokeWidth="7" fill="none" strokeLinecap="round" />
        <text x="104" y="150" fill={PAPER} fontFamily="'Space Grotesk', system-ui, sans-serif" fontWeight="700" fontSize="20" letterSpacing="1" transform="rotate(-8 104 150)">RB</text>
        <path d="M112 220 L44 236 L60 246 L170 230 Z" fill="url(#rb-tail)" />

        {/* Fuselage */}
        <path
            d="M150 192 L640 192 C694 192 734 206 752 230 C736 252 696 262 640 262 L196 262 C152 262 118 248 96 226 L84 200 C104 195 126 192 150 192 Z"
            fill="url(#rb-fuselage)"
            stroke="#CFC7B6"
            strokeWidth="1.5"
        />
        {/* Livery: a navy belly stripe with a red pinstripe */}
        <path d="M118 238 C150 246 180 248 210 248 L688 248 C708 246 726 242 740 236 C722 254 690 262 640 262 L196 262 C162 262 134 254 118 238 Z" fill={INK} />
        <path d="M150 242 L700 242" stroke={RED} strokeWidth="3" strokeLinecap="round" />

        {/* Cockpit and windows */}
        <path d="M694 206 L724 212 L738 226 L700 224 Z" fill={WINDOW} />
        {WINDOWS.map((x) => <rect key={x} x={x} y="212" width="9" height="12" rx="4" fill={WINDOW} />)}
        <rect x="186" y="204" width="16" height="32" rx="4" fill="none" stroke="#B9B2A3" strokeWidth="2" />
        <rect x="652" y="204" width="16" height="32" rx="4" fill="none" stroke="#B9B2A3" strokeWidth="2" />

        <text x="318" y="235" fill={INK} fontFamily="'Space Grotesk', system-ui, sans-serif" fontWeight="700" fontSize="12.5" letterSpacing="3.5">RIGHT BROTHER AIRWAYS</text>

        {/* Near wing and engine */}
        <path d="M380 246 L300 340 L340 340 L486 250 Z" fill="#D8D1C1" stroke="#BDB5A4" strokeWidth="1.5" />
        <path d="M300 340 L284 352 L318 352 L340 340 Z" fill={RED} />
        <ellipse cx="392" cy="284" rx="44" ry="17" fill="#C9C1AF" />
        <ellipse cx="350" cy="284" rx="8" ry="15" fill={INK} />
        <path d="M356 270 L436 274" stroke={RED} strokeWidth="3" strokeLinecap="round" />
    </svg>
);

export default PlaneArt;
